import { Modal, Spin, Progress } from "antd";

/**
 * ProgressModal
 * props:
 *  - loading: bool (uploading phase)
 *  - processing: bool (ML processing phase)
 *  - activeStep: number (1..totalSteps) - optional, can be provided by redux
 *  - totalSteps: number (defaults to 4)
 *  - onClose: fn
 *  - disableClose: bool
 */
const ProgressModal = ({
  loading = false,
  processing = false,
  activeStep: activeStepProp,
  totalSteps = 4,
  onClose,
  disableClose = true,
  children,
}) => {
  const visible = !!loading || !!processing;

  // derive active step: prefer prop, else 1 for upload or processing start
  let activeStep = typeof activeStepProp === "number" ? activeStepProp : 1;
  if (!processing && loading) activeStep = 0; // during upload we show upload UI (no numbered steps)

  const percent = processing
    ? Math.round((Math.min(Math.max(activeStep, 1), totalSteps) / totalSteps) * 100)
    : 0;

  const handleCancel = () => {
    if (disableClose) return;
    if (typeof onClose === "function") onClose();
  };

  // step messages exactly as user requested
  const stepMessages = [
    "Dataset Registered Successfully",
    "Preprocessing Experimental Data",
    "Typing Columns — Pattern Recognition Active",
    "KNN Imputation in Progress",
  ];

  return (
    <Modal
      title={
        loading
          ? "🧙‍♂️ Wizard at work... Upload potion bubbling"
          : processing
          ? "Processing"
          : "Progress"
      }
      open={visible}
      onCancel={handleCancel}
      footer={null}
      destroyOnHidden
      centered
      styles={{
        mask: {
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
          backgroundColor: "rgba(15, 23, 42, 0.6)",
        },
      }}
      maskClosable={!disableClose}
      closable={!disableClose}
    >
      <div style={{ textAlign: "center" }}>
        {loading && (
          <>
            <div style={{ marginBottom: 12 }}>
              <Spin tip="Initializing upload..." />
            </div>
            <div style={{ marginBottom: 8 }}>
              Your data is brewing in the cloud cauldron!
            </div>
          </>
        )}

        {processing && (
          <>
            <div style={{ marginBottom: 12 }}>
              <Spin
                tip={
                  activeStep === 2
                    ? "Preprocessing (this may take ~20s)..."
                    : "Processing..."
                }
              />
            </div>

            <Progress
              percent={percent}
              status={activeStep < totalSteps ? "active" : "normal"}
              style={{ marginBottom: 12 }}
            />

            <div style={{ marginBottom: 12 }}>
              Step {Math.min(activeStep, totalSteps)} of {totalSteps}
            </div>

            <div style={{ textAlign: "left", margin: "0 auto", maxWidth: 560 }}>
              {stepMessages.map((msg, idx) => {
                const stepIndex = idx + 1;
                const isDone = stepIndex < activeStep;
                const isActive = stepIndex === activeStep;

                return (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 0",
                      borderRadius: 6,
                      background: isActive ? "rgba(0,0,0,0.03)" : "transparent",
                      fontWeight: isActive ? 600 : 500,
                    }}
                  >
                    <div style={{ width: 28, textAlign: "center" }}>
                      {isDone ? "✅" : isActive ? "⏳" : "◻️"}
                    </div>
                    <div>
                      <div style={{ lineHeight: 1.1 }}>{msg}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {children && (
              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  borderRadius: 8,
                  background: "rgba(0,0,0,0.04)",
                }}
              >
                {children}
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default ProgressModal;
